import { useCallback, useEffect, useRef, useSyncExternalStore } from "react";
import { ACTIVITY_DEBOUNCE_MS } from "auth/constants";
import { refreshWhileActive } from "auth/refreshSession";
import { authStore } from "auth/authStore";

const ACTIVITY_EVENTS = ["mousemove", "keydown", "click", "scroll", "focus"];

/** How often to reset the idle timer from pointer/keyboard events. */
const IDLE_ACTIVITY_THROTTLE_MS = 1_000;

/**
 * Tracks user interaction — resets idle timers often, refreshes session less often.
 */
export default function useIdleTimer(enabled) {
  const lastIdleRecordRef = useRef(0);
  const lastRefreshRef = useRef(0);

  const handleActivity = useCallback(() => {
    if (!enabled) {
      return;
    }

    const now = Date.now();

    if (now - lastIdleRecordRef.current >= IDLE_ACTIVITY_THROTTLE_MS) {
      lastIdleRecordRef.current = now;
      authStore.recordActivity();
    }

    if (now - lastRefreshRef.current >= ACTIVITY_DEBOUNCE_MS) {
      lastRefreshRef.current = now;
      refreshWhileActive("activity").catch(() => {});
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    ACTIVITY_EVENTS.forEach((event) => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    return () => {
      ACTIVITY_EVENTS.forEach((event) => {
        window.removeEventListener(event, handleActivity);
      });
    };
  }, [enabled, handleActivity]);
}

export function handleActivityRefresh() {
  authStore.recordActivity();
  refreshWhileActive("activity").catch(() => {});
}
